// Module-singleton GPS watch.
//
// Lives OUTSIDE React so background tracking continues across route navigation
// and component unmounts. The Tracking screen is just a control surface — it
// reads/writes through here instead of owning the WatchHandle itself.
//
// Why this matters: the LocationBG Swift plugin keeps CoreLocation alive in
// the background only as long as nobody calls stopWatch(). When the previous
// implementation tied the WatchHandle to a useRef and called .stop() on
// unmount, navigating away from /tracking killed background GPS the moment
// the user switched tabs. Now stopWatch() runs only when the user explicitly
// taps "Stop tracking" or signs out.

import { watchPosition, getCurrentPing, type WatchHandle, type Ping } from "./geolocation";
import { getGpsAdaptive } from "@/lib/prefs";
import { sendOrQueuePing, drainPingOutbox } from "./ping-outbox";
import { addConnectionListener } from "./network";
// Live Activity is owned exclusively by liveActivityController (gated on
// real trip-recording state). Do NOT start it from the GPS watcher.
import { hapticImpact } from "./haptics";
import { liveActivityController } from "@/lib/live-activity/controller";
import { markGpsFix, markGpsError } from "./gps-status";

type State = {
  handle: WatchHandle | null;
  tripId: string | null;
  starting: boolean;
  netListener: { remove: () => void } | null;
  // Foreground-only soft heartbeat. Pumps a one-shot getCurrentPosition into
  // the watcher's existing consider() gate at the preset's stoppedIntervalMs
  // while the app is visible. Does NOT promise stationary background tracking
  // — that requires a real native heartbeat (see .lovable/native-pending.md).
  heartbeatTimer: ReturnType<typeof setInterval> | null;
  heartbeatIntervalMs: number;
  visibilityListener: (() => void) | null;
};

const state: State = {
  handle: null,
  tripId: null,
  starting: false,
  netListener: null,
  heartbeatTimer: null,
  heartbeatIntervalMs: 2 * 60_000,
  visibilityListener: null,
};

/** Map ping source → backend enum (recordLocationPing schema is stricter). */
function mapSource(s: Ping["source"]): "foreground" | "background" | "native_ios_background" {
  if (s === "native_ios_background") return "native_ios_background";
  return "foreground";
}

/**
 * Idempotent. Safe to call repeatedly — second call with same tripId is a no-op.
 *
 * `suppressFirstFix`: when true, the first incoming GPS fix after the watch
 * is armed is consumed as a baseline only — `lastEmitted` is seeded but
 * `onPing` is NOT called. Used exclusively by the preset-change reapply path
 * (`reapplyActiveWatchAdaptive`) so changing GPS preset never creates a
 * ghost route point. Normal Start and Resume MUST NOT pass this flag.
 */
export async function startActiveWatch(
  tripId: string,
  opts?: { suppressFirstFix?: boolean; source?: string },
): Promise<void> {
  if (state.starting) return;
  if (state.handle && state.tripId === tripId) return;
  if (state.handle && state.tripId !== tripId) {
    // Trip changed mid-session — tear down old watch first.
    await stopActiveWatch();
  }
  state.starting = true;
  state.tripId = tripId;
  void hapticImpact("medium");
  try {
    // Preset changes mid-trip are picked up via reapplyActiveWatchAdaptive()
    // — see GpsRateSheet.
    const adaptive = getGpsAdaptive();
    const adaptiveWithFlags = { ...adaptive, suppressFirstFix: !!opts?.suppressFirstFix };
    if (opts?.suppressFirstFix) {
      console.info(`[active-watch] startActiveWatch suppressFirstFix=true source=${opts?.source ?? "?"}`);
    }
    state.handle = await watchPosition(
      async (p: Ping) => {
        // Mark a GPS fix BEFORE attempting send. The OfflineTrackingStatus
        // panel relies on this to prove to the traveler that GPS is working
        // even when uploads are queued.
        try { markGpsFix(p.lat, p.lng, p.accuracy ?? null, Date.now()); } catch { /* ignore */ }
        // sendOrQueuePing handles offline: it tries the server, falls back to
        // an IndexedDB outbox on failure, and drains automatically when the
        // network returns. Pings are never dropped silently.
        try {
          await sendOrQueuePing({
            tripId,
            lat: p.lat,
            lng: p.lng,
            accuracy: p.accuracy ?? null,
            altitude: p.altitude ?? null,
            speed: p.speed ?? null,
            heading: p.heading ?? null,
            capturedAt: new Date().toISOString(),
            source: mapSource(p.source),
          });
        } catch (e) {
          console.warn("[active-watch] ping handling failed", e);
        }
      },
      (msg) => {
        console.warn("[active-watch] geolocation error", msg);
        try { markGpsError(msg, Date.now()); } catch { /* ignore */ }
      },
      adaptiveWithFlags,
    );

    // Drain the outbox whenever the network comes back (wifi or cellular).
    // Mongolia/desert scenario: user spends 8 hours offline, then re-enters
    // a town with cell signal → queue flushes in the background.
    if (!state.netListener) {
      state.netListener = addConnectionListener((t) => {
        if (t === "wifi" || t === "cellular") {
          void drainPingOutbox();
        }
      });
    }
    // Also attempt a drain on start, in case a previous session left pings behind.
    void drainPingOutbox();
    // Dynamic Island / Live Activity is started by liveActivityController
    // only when a trip is actively being recorded — never from the GPS
    // watcher. Background location alone should not surface a pill.

    // Foreground-only soft heartbeat. While the app is visible, pump a
    // one-shot getCurrentPosition through the watcher's consider() gate at
    // the preset's stoppedIntervalMs. This refreshes "Last GPS fix" when the
    // user is stationary AND has the app open — iOS otherwise delivers no
    // CoreLocation callbacks until the distanceFilter is crossed. Does NOT
    // run while backgrounded / phone locked (iOS suspends JS timers and
    // refuses getCurrentPosition then). A real native background heartbeat
    // is staged in .lovable/native-pending.md for the next iOS submission.
    state.heartbeatIntervalMs = Math.max(15_000, adaptive.stoppedIntervalMs ?? 2 * 60_000);
    startForegroundHeartbeat();
  } finally {
    state.starting = false;
  }
}

function pumpHeartbeatOnce() {
  if (!state.handle) return;
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  void (async () => {
    try {
      const p = await getCurrentPing();
      try { markGpsFix(p.lat, p.lng, p.accuracy ?? null, Date.now()); } catch { /* ignore */ }
      // Pump through the watcher's adaptive gate. consider() decides whether
      // this point qualifies (movement >= preset distance OR stopped+interval
      // elapsed). If it doesn't qualify, nothing is emitted — no duplicate
      // pings, no bypass of distance/quality rules.
      state.handle?.pump(p);
    } catch (e) {
      // Foreground-only timer; iOS denying getCurrentPosition while the app
      // is briefly backgrounding is normal. Don't surface as an error.
      console.debug("[active-watch] foreground heartbeat skipped", e);
    }
  })();
}

function startForegroundHeartbeat() {
  stopForegroundHeartbeat();
  if (typeof window === "undefined") return;
  state.heartbeatTimer = setInterval(pumpHeartbeatOnce, state.heartbeatIntervalMs);
  state.visibilityListener = () => {
    if (document.visibilityState === "visible" && state.handle) {
      // Resumed — fire one immediately so "Last GPS fix" snaps fresh on
      // re-open, then resume the normal interval.
      pumpHeartbeatOnce();
    }
  };
  document.addEventListener("visibilitychange", state.visibilityListener);
}

function stopForegroundHeartbeat() {
  if (state.heartbeatTimer != null) {
    clearInterval(state.heartbeatTimer);
    state.heartbeatTimer = null;
  }
  if (state.visibilityListener && typeof document !== "undefined") {
    try { document.removeEventListener("visibilitychange", state.visibilityListener); } catch { /* ignore */ }
    state.visibilityListener = null;
  }
}

/**
 * Tear down the active watch. Safe to call when not running.
 *
 * `keepLiveActivity`: when true, do NOT dismiss the Apple Live Activity. Used
 * by pause and cadence-change paths where LocationBG stops/restarts but the
 * Dynamic Island / Lock Screen surface should stay alive and just update its
 * state (isPaused / new preset). Default false preserves the original
 * Stop-tracking behavior (dismiss + latch against resurrect).
 */
export async function stopActiveWatch(
  opts?: { keepLiveActivity?: boolean; source?: string },
): Promise<void> {
  const h = state.handle;
  state.handle = null;
  state.tripId = null;
  stopForegroundHeartbeat();
  void hapticImpact("medium");
  if (h) {
    try { await h.stop(); } catch { /* ignore */ }
  }
  if (state.netListener) {
    try { state.netListener.remove(); } catch { /* ignore */ }
    state.netListener = null;
  }
  // One last drain attempt so any pings captured between the last successful
  // send and stop don't sit forever.
  void drainPingOutbox();
  if (!opts?.keepLiveActivity) {
    // End Live Activity / Dynamic Island. Idempotent; iOS-only no-op on web.
    // End Live Activity via the controller, which also latches against
    // upload-drain signals trying to resurrect the widget right after Stop.
    try { liveActivityController.userDismiss({ source: opts?.source ?? "stopActiveWatch" }); } catch { /* non-fatal */ }
  }
}


export function isActiveWatchRunning(): boolean {
  return !!state.handle;
}

export function getActiveWatchTripId(): string | null {
  return state.tripId;
}

/**
 * Re-arm the active watch so a freshly-saved GPS preset / custom distance
 * takes effect immediately, without making the user tap Stop → Start.
 *
 * No-op when nothing is running. Brief (<1s) gap in GPS callbacks during the
 * stop+start swap; outbox + Live Activity are preserved (Live Activity is
 * re-started by startActiveWatch).
 */
export async function reapplyActiveWatchAdaptive(
  opts?: { keepLiveActivity?: boolean; source?: string; suppressFirstFix?: boolean },
): Promise<boolean> {
  if (!state.handle || !state.tripId) return false;
  const tripId = state.tripId;
  await stopActiveWatch({ keepLiveActivity: opts?.keepLiveActivity, source: opts?.source ?? "reapplyActiveWatchAdaptive" });
  // suppressFirstFix is for preset-change reapply only — see startActiveWatch
  // docstring. Defaults to false; preset-change callers must pass true.
  await startActiveWatch(tripId, {
    suppressFirstFix: !!opts?.suppressFirstFix,
    source: opts?.source ?? "reapplyActiveWatchAdaptive",
  });
  return true;
}

// ---------- Pause / Resume ----------------------------------------------------
//
// Pause stops the underlying CoreLocation watcher but preserves the trip
// session: tripId, outbox listener, queued points, startedAt, and the Live
// Activity all stay alive. Resume re-arms the watcher with the same tripId.
// No new session is created; no duplicate trip is recorded.
//
// Live Activity is informed via the controller so the Lock Screen / Dynamic
// Island pill flips its isPaused state without being dismissed.

let pausedTripId: string | null = null;

export async function pauseActiveWatch(opts?: { source?: string }): Promise<boolean> {
  const source = opts?.source ?? "pauseActiveWatch";
  if (!state.handle || !state.tripId) {
    console.warn(`[active-watch] pause requested but no active watch (source=${source})`);
    return false;
  }
  pausedTripId = state.tripId;
  const h = state.handle;
  state.handle = null;
  stopForegroundHeartbeat();
  // Intentionally KEEP: state.tripId, state.netListener (drain on reconnect
  // still useful while paused), Live Activity, outbox.
  try { await h.stop(); } catch { /* ignore */ }
  console.info(`[active-watch] paused tripId=${pausedTripId} source=${source} — keeping LA, outbox, tripId`);
  try { liveActivityController.onTrackingPaused(true); } catch { /* non-fatal */ }
  return true;
}

export async function resumeActiveWatch(opts?: { source?: string }): Promise<boolean> {
  const source = opts?.source ?? "resumeActiveWatch";
  const tripId = state.tripId ?? pausedTripId;
  if (!tripId) {
    console.warn(`[active-watch] resume requested but no tripId remembered (source=${source})`);
    return false;
  }
  if (state.handle) {
    console.info(`[active-watch] resume noop — watch already running (source=${source})`);
    try { liveActivityController.onTrackingPaused(false); } catch { /* non-fatal */ }
    return true;
  }
  console.info(`[active-watch] resuming tripId=${tripId} source=${source}`);
  // Resume MUST NOT pass suppressFirstFix — the user expects an immediate
  // route point when they un-pause.
  await startActiveWatch(tripId, { source });
  pausedTripId = null;
  try { liveActivityController.onTrackingPaused(false); } catch { /* non-fatal */ }
  return true;
}

export function isActiveWatchPaused(): boolean {
  return !state.handle && pausedTripId != null;
}


